/**
 * محتوای مرکزی راهنمای سامانه ارجان پیمان — **مبنای نقش (role-aware)**
 *
 * HELP_BY_ROLE[role][route] = { title, icon, intro, sections: [{title, body[], tip?}] }
 * helpFor(route, role)        → محتوای راهنمای همان صفحه برای نقشِ واردشده (یا null)
 * roleHelpRoutes(role)        → فهرست صفحاتی که برای این نقش راهنما دارند
 *
 * استفاده: کشوی راهنمای هر صفحه (؟ در نوار بالا) + صفحهٔ کامل «راهنما» + تور آموزشی
 */

export const ROLE_LABEL = {
  Contractor: 'پیمانکار',
  Consultant: 'مشاور',
  Supervisor: 'ناظر',
  Department: 'سازمان (دستگاه اجرایی)',
  DepartmentManager: 'مدیر سازمان',
  Admin: 'ادمین',
}

/* ═══════════════ بلوکهای مشترک بین نقشها ═══════════════ */

const messagesHelp = {
  title: 'مرکز پیام',
  icon: '✉️',
  intro: 'پیام‌رسانیِ سامانه: گفتگو با سازمان، پیمانکار، ناظر و مشاور.',
  sections: [
    {
      title: 'چیدمانِ مرکز پیام',
      body: [
        'سمت راست: فهرستِ گفتگوها (هر گفتگو = یک طرفِ مقابل + یک موضوع). نقطهٔ قرمز یعنی پیامِ خوانده‌نشده دارید.',
        'سمت چپ: حلقهٔ گفتگوی انتخاب‌شده؛ با کلیک روی هر گفتگو از فهرستِ کنار، همان باز می‌شود.',
        'دکمهٔ «گفتگوی جدید» ⇒ انتخابِ نوعِ مخاطب و سازمان، سپس انتخابِ مخاطب و نوشتنِ موضوع.',
        'پیام‌ها به ترتیبِ زمانی (قدیمی بالا، جدید پایین) نمایش داده می‌شوند.',
      ],
      tip: 'روی موبایل، ابتدا یک گفتگو را از فهرست انتخاب کنید؛ حلقهٔ پیام همان‌جا باز می‌شود.',
    },
    {
      title: 'چه کسی را می‌توانم پیام بدهم؟',
      body: [
        'پیمانکار / ناظر / مشاور: ادمین، کاربرِ سازمان، مدیرِ سازمان، و طرفِ قراردادِ مشخص‌شده (ناظر/مشاور/پیمانکار).',
        'کاربرِ سازمان / مدیرِ سازمان / ادمین: همهٔ کاربران.',
        'قاعده: تبادلِ پیام میان پیمانکار/ناظر/مشاور حتماً باید مربوط به یک قراردادِ مشخص باشد؛ ولی پیام به سازمان، مدیرِ سازمان یا ادمین بدونِ قرارداد هم مجاز است.',
      ],
      tip: 'اگر قرارداد را انتخاب نکنید و طرفِ مقابل پیمانکار/ناظر/مشاور باشد، سامانه هشدار می‌دهد.',
    },
    {
      title: 'وضعیتِ هر پیام',
      body: [
        'کنارِ هر پیام یک برچسب رنگی می‌بینید: «ارسال شده» (خاکستری)، «رسیده» (آبی) و «رویت شده» (سبز).',
        'پیامِ خوانده‌نشده تا کلیک شما محو (blur) می‌ماند و با همان کلیک، زمانِ رویت ثبت می‌شود.',
      ],
    },
    {
      title: 'اعلان و پیامک',
      body: [
        'زنگولهٔ اعلان‌ها در نوارِ بالا: حوالهٔ جدید، پیامِ جدید، انقضای اشتراک و ... (با عددِ خوانده‌نشده‌ها).',
        'پیامکِ «حواله جدید» و وضعیتِ پردازشِ آپلود هم به‌صورت پیامک ارسال می‌شود.',
        'نوتیفِ مرورگر (روی کامپیوتر/موبایل) فقط اطلاع‌رسانی است و متنِ پیام را نشان نمی‌دهد.',
      ],
    },
  ],
}

const editHelp = {
  title: 'ویرایش مشخصات',
  icon: '👤',
  intro: 'مشخصات حساب خودتان را اینجا ویرایش کنید.',
  sections: [
    {
      title: 'ویرایش مشخصات',
      body: [
        'تغییر نام کاربری، گذرواژه، موبایل و آپلود لوگو/عکسِ پروفایل.',
        'شناسه ملی و شمارهٔ اقتصادی فقط از سمت شرکت (ادمین) قابل تغییر است.',
      ],
      tip: 'برای اجرای دوبارهٔ تور آموزشی، دکمهٔ «تور آموزشی» در همین صفحه است.',
    },
    {
      title: 'شمارهٔ همراه',
      body: [
        'تا وقتی شمارهٔ همراهِ خودتان ثبت نشده باشد، پنل کامل باز نمی‌شود.',
        'شماره باید ۱۱ رقم و با ۰۹ آغاز شود؛ ارقام فارسی هم پذیرفته می‌شود.',
      ],
      tip: 'این اجبار فقط برای خودِ شماست؛ وقتی ادمین یا سازمان از پنلِ خودش واردِ پنلِ شما می‌شود، نیازی به ثبت شماره نیست.',
    },
  ],
}

const aboutHelp = {
  title: 'درباره ما',
  icon: 'ℹ️',
  intro: 'معرفی شرکت ارجان آرایه پویا و راههای ارتباطی.',
  sections: [
    {
      title: 'ارتباط با ما',
      body: [
        'از همین صفحه (یا دکمهٔ «تماس با ما» در منو) می‌توانید با پشتیبانی تماس بگیرید.',
      ],
    },
  ],
}

/* ═══════════════ پیمانکار ═══════════════ */

const CONTRACTOR = {
  '/': {
    title: 'داشبورد پیمانکار — قراردادهای من',
    icon: '🏠',
    intro: 'فهرست قراردادهای شما با سازمانهای طرف قرارداد؛ هر قرارداد یک کارت مستقل است.',
    sections: [
      {
        title: 'کارتِ مشخصات (بالای صفحه)',
        body: [
          'نام، شناسهٔ ملی، شمارهٔ ثبت، کد معاملاتی و کد اقتصادی شما.',
          'دکمهٔ «محدودیت‌ها» محدودیت‌های ثبت‌شدهٔ کلِ حسابِ شما را نشان می‌دهد.',
        ],
      },
      {
        title: 'معرفی ناظر و مشاور',
        body: [
          'کارتِ «معرفی ناظر/مشاور» بالای فهرست: نام و شمارهٔ همراهِ ناظر یا مشاور را وارد کنید.',
          'برای او پیامکِ معرفی ارسال می‌شود؛ او با پاسخ ۱ می‌پذیرد و با ۲ رد می‌کند.',
          'پس از پذیرش، او به‌عنوان ناظر/مشاورِ آن قرارداد ثبت می‌شود و گزارش‌های نظارتی برایتان باز می‌شود.',
        ],
        tip: 'تا وقتی ناظر/مشاور معرفی نشده باشد، کارتِ قرارداد «ناظر/مشاور» ندارد.',
      },
      {
        title: 'کارت هر قرارداد',
        body: [
          'بالای کارت: نام سازمان، شماره و تاریخ قرارداد.',
          'موضوع قرارداد و چیپ «به‌روزرسانی سازمان» را می‌بینید — رنگ این چیپ نشان میدهد سازمان چقدر اخیر اطلاعات را آپلود کرده: 🟢 تا ۳۰ روز، 🟠 تا ۹۰ روز، 🔴 قدیمی‌تر.',
          'مشاور و ناظر قرارداد هم کنار آن دیده می‌شوند.',
        ],
        tip: 'اگر چیپ به‌روزرسانی قرمز است یعنی سازمان مدتی است اطلاعات جدید نفرستاده — برای پیگیری با آنها تماس بگیرید.',
      },
      {
        title: 'دکمه‌های گزارشِ هر قرارداد',
        body: [
          'زیر هر کارت: خلاصه، ضمانت‌نامه، سپرده، صورت‌وضعیت، اسناد خزانه، حواله‌ها، اسناد، اعلامیه‌ها، محدودیت‌ها و بیمه‌های پرداختی.',
          'دکمهٔ کم‌رنگ یعنی آن گزارش برای این قرارداد داده‌ای ندارد. آیکن قفل یعنی تا اشتراک فعال نشود باز نمی‌شود (و با کلیک، صفحهٔ عضویت باز می‌شود).',
          'در گزارشِ «صورت‌وضعیت» می‌توانید کلِ صورت‌وضعیت‌های قرارداد را یکجا (چاپ PDF یا اکسل) بگیرید؛ در سایر گزارش‌ها هم جستجو و فیلترِ سال مالی هست.',
          'دکمهٔ «گزارش پیام‌ها» پیام‌های همان قرارداد را نشان می‌دهد.',
          '«بیمه‌های پرداختی»: فهرستِ سطرهای بیمه از اسناد و انتقال‌ها با جمعِ هر ستون؛ «چاپ» یک لیستِ راست‌چینِ آمادهٔ چاپ می‌سازد (اگر ستون‌ها زیاد باشد صفحه A3 افقی می‌شود تا خوانا بماند) و «خروجی Excel» همان جدول را می‌دهد.',
        ],
        tip: 'خروجی اکسلِ هر گزارش از همان نتیجهٔ فیلتر گرفته می‌شود.',
      },
      {
        title: 'سه مبلغِ قرارداد',
        body: [
          'در «خلاصه» و گزارش‌ها، مبلغ سه‌تکه نمایش داده می‌شود: مبلغ اولیه، افزایش/کاهش، و جمع مبلغ.',
          'افزایش/کاهش از فیلدِ IncreaseDecreaseCost در فایلِ خروجیِ جام ارجان خوانده می‌شود.',
        ],
      },
      {
        title: 'جستجو، مرتب‌سازی و خروجی',
        body: [
          'بالای لیست، جستجوی زنده (سازمان، شماره قرارداد، موضوع) و مرتب‌سازی (تازه‌ترین به‌روزرسانی سازمان، شماره، تاریخ) دارید.',
          'اگر چند سازمان داشته باشید، فیلترِ «سازمان» هم اضافه می‌شود.',
          '«دانلود اکسل»: فهرست قراردادها با تاریخ شمسی و کیوآرکد لینک ۳۰ روزه.',
          '«گزارش مالی»: گزارشِ تجمیعیِ سه‌لایه از همهٔ قراردادهای شما (نیازمند اشتراک فعال).',
        ],
      },
    ],
  },

  '/user-management': {
    title: 'مدیریت کاربر (پیمانکار)',
    icon: '🗂',
    intro: 'حساب شما: لوگو/عکس، کلمهٔ عبور، وضعیت اشتراک و تاریخچهٔ پرداخت‌ها.',
    sections: [
      {
        title: 'لوگو و کلمهٔ عبور',
        body: [
          '«ویرایش مشخصات کاربری»: نام کاربری، کلمهٔ عبور و موبایل.',
          '«انتخاب فایل یا عکس با دوربین»: ثبت لوگو/عکسِ پروفایل.',
        ],
      },
      {
        title: 'وضعیت اشتراک و فاکتور',
        body: [
          'چیپ «فعال» یا «منقضی شده» + تاریخ انقضا و روزهای باقی‌مانده.',
          'جدولِ پرداخت‌ها: تاریخ، مبلغ، شمارهٔ پیگیری و دکمهٔ «چاپ فاکتور» (PDF).',
        ],
        tip: 'برای خریدِ اشتراک، کافی است یکی از دکمه‌های قفلِ گزارش‌ها را بزنید تا صفحهٔ عضویت باز شود.',
      },
    ],
  },

  '/messages': messagesHelp,
  '/edit': editHelp,
  '/about': aboutHelp,
}

/* ═══════════════ سازمان (دستگاه اجرایی) ═══════════════ */

const DEPARTMENT = {
  '/': {
    title: 'بارگذاری فایل — سازمان',
    icon: '📤',
    intro: 'فایل خروجی نرم‌افزار (zip) را اینجا بارگذاری می‌کنید تا اطلاعاتِ قراردادها وارد سامانه شود.',
    sections: [
      {
        title: 'کارتِ سازمان (بالای صفحه)',
        body: [
          'نام سازمان، شناسهٔ ملی و «آخرین بارگذاری موفق».',
          'چیپِ «در حال پردازش» یعنی آپلودِ شما در جریان است.',
        ],
      },
      {
        title: 'بارگذاری',
        body: [
          'فایل zip خروجی سامانه را در کادر رها کنید و «آپلود» را بزنید (حداکثر ۴۰۰ مگابایت).',
          'حین پردازش، زیر چیپ «در حال پردازش» نام مرحله + درصد + نوار پیشرفت می‌بینید.',
        ],
        tip: 'مرحلهٔ «اسناد» برای فایلهای بزرگ چند دقیقه طول می‌کشد — عادی است.',
      },
      {
        title: 'در صف',
        body: [
          'اگر پیام «در صف — منتظر نوبت پردازش» دیدید یعنی آپلود دیگری در جریان است؛ فایل شما بعد از آن پردازش می‌شود.',
        ],
      },
      {
        title: 'پس از پایان',
        body: [
          'در پایان، تاریخ «آخرین بارگذاری موفق» به‌روز می‌شود و اطلاعات برای پیمانکاران/ناظران همان قرارداد باز می‌شود.',
          'فیلدِ «افزایش/کاهش» مبلغِ قرارداد هم از فایلِ شما خوانده می‌شود.',
          'اطلاعاتِ ناظر و مشاور هم اگر در فایل باشد ثبت می‌شود.',
        ],
      },
      {
        title: 'مدیران سازمان',
        body: [
          'کارتِ «مدیران سازمان» پایین همین صفحه: برای هر مدیر یک حساب با نقشِ «مدیر سازمان» بسازید.',
          'نامِ کاربری = کد ملیِ مدیر و رمز را خودِ سازمان تعیین می‌کند؛ مدیر می‌تواند بعداً رمزش را عوض کند.',
          'مدیر سازمان همهٔ امکاناتِ سازمان را دارد بجزِ آپلودِ اطلاعات.',
          'برای هر مدیر می‌توانید رمزِ جدید تعیین کنید، شمارهٔ تلفنش را اصلاح کنید یا حسابش را حذف کنید.',
        ],
      },
    ],
  },

  '/contractors': {
    title: 'پیمانکاران (سازمان)',
    icon: '👥',
    intro: 'فهرست پیمانکاران طرف قرارداد دستگاه اجرایی.',
    sections: [
      {
        title: 'جستجو و صفحه‌بندی',
        body: [
          'کادرِ جستجوی بالای صفحه (نام یا شناسهٔ ملی) و صفحه‌بندی در پایین جدول.',
        ],
      },
      {
        title: 'کلید ورود',
        body: [
          'روبروی هر پیمانکار دکمهٔ «ورود» — با نقش همان پیمانکار وارد سامانه می‌شوید تا وضعیت را از دید او ببینید.',
          'با «خروج» به پنلِ خودِ سازمان برمی‌گردید (نه خروجِ کامل).',
        ],
      },
      {
        title: 'ستون‌های جدول',
        body: [
          'تعداد قرارداد، تعداد حواله‌های یک‌سالِ گذشته، شمارهٔ ثبت، شناسهٔ ملی، کدِ اقتصادی و کدِ معاملاتی.',
        ],
      },
    ],
  },

  '/user-management': {
    title: 'مدیریت کاربر (سازمان)',
    icon: '🗂',
    intro: 'حساب سازمان شما: نام اختصاری، نام کاربری، گذرواژه و لوگو.',
    sections: [
      {
        title: 'تنظیمات حساب سازمان',
        body: [
          'نام اختصاری سازمان کنار نام سازمان در همهٔ جدولها نمایش داده می‌شود.',
          'تغییر نام کاربری و گذرواژهٔ حساب سازمان از همین‌جاست (گذرواژه اختیاری است — اگر خالی بماند تغییر نمی‌کند).',
        ],
      },
      {
        title: 'لوگو / عکس پروفایل',
        body: [
          'با دکمهٔ «انتخاب فایل یا عکس با دوربین» لوگوی سازمان را ثبت کنید.',
        ],
      },
    ],
  },

  '/messages': messagesHelp,
  '/edit': editHelp,
  '/about': aboutHelp,
}

/* ═══════════════ مدیر سازمان ═══════════════ */

const DEPARTMENT_MANAGER = {
  '/': {
    title: 'داشبورد مدیر سازمان',
    icon: '🏛',
    intro: 'نمای کلیِ سازمانِ شما: قراردادها، پیمانکاران، حواله‌ها و مدیران — به‌همراه فهرستِ قراردادهای هر پیمانکار.',
    sections: [
      {
        title: 'سربرگ و کارت‌های آماری',
        body: [
          'سربرگ: نام و نامِ اختصاریِ سازمان، شناسهٔ ملی و «آخرین بارگذاری موفق».',
          'چهار کارت: تعداد قراردادها، تعداد پیمانکاران، حواله‌های یک‌ساله و تعداد مدیرانِ سازمان.',
        ],
      },
      {
        title: 'جدولِ پیمانکاران (سمت راست)',
        body: [
          'پیمانکارانِ سازمان به‌ترتیبِ «آخرین حواله» (تازه‌ترین بالا) فهرست می‌شوند؛ کسانی که حواله ندارند انتهای فهرست‌اند.',
          'کادرِ جستجو (با دکمهٔ ×) نام یا کد ملیِ پیمانکار را می‌گیرد.',
          'کلیدِ «ورود» کنارِ هر نام ⇒ با نقشِ همان پیمانکار واردِ پنلش می‌شوید تا وضعیت را از دید او ببینید. با «خروج» به داشبوردِ خودِ شما برمی‌گردید.',
        ],
        tip: 'با کلیک روی نامِ پیمانکار، جدولِ سمتِ چپ به قراردادهای همان پیمانکار تغییر می‌کند.',
      },
      {
        title: 'جدولِ قراردادها (سمت چپ)',
        body: [
          'قراردادهای پیمانکارِ انتخاب‌شده، به‌ترتیبِ نزولیِ تاریخِ قرارداد.',
          'ستون‌ها: شماره، تاریخ، مبلغ اولیه، افزایش/کاهش، جمع مبلغ، جمع صورت‌وضعیتِ عادی، پرداخت‌شده و مانده.',
          'کادرِ جستجو (با دکمهٔ ×) در شماره یا موضوعِ قرارداد جستجو می‌کند.',
        ],
        tip: '«جمع صورت‌وضعیت عادی» فقط صورت‌وضعیت‌های عادی را در بر می‌گیرد؛ «مانده» = جمع مبلغ منهای پرداخت‌شده.',
      },
      {
        title: 'محدودیتِ این نقش',
        body: [
          'مدیرِ سازمان همهٔ امکاناتِ کاربرِ سازمان را دارد بجزِ آپلودِ اطلاعات.',
          'ساخت، ویرایش و حذفِ مدیران با خودِ کاربرِ سازمان است (کارتِ «مدیران سازمان» در صفحهٔ اصلیِ سازمان).',
        ],
      },
    ],
  },

  '/contractors': {
    title: 'پیمانکاران (مدیر سازمان)',
    icon: '👥',
    intro: 'فهرست پیمانکاران طرف قرارداد دستگاه اجرایی.',
    sections: [
      {
        title: 'جستجو و صفحه‌بندی',
        body: [
          'کادرِ جستجوی بالای صفحه (نام یا شناسهٔ ملی) و صفحه‌بندی در پایین جدول.',
        ],
      },
      {
        title: 'کلید ورود',
        body: [
          'روبروی هر پیمانکار دکمهٔ «ورود» — با نقش همان پیمانکار وارد سامانه می‌شوید تا وضعیت را از دید او ببینید.',
          'با «خروج» به داشبوردِ خودِ شما برمی‌گردید.',
        ],
      },
    ],
  },

  '/user-management': {
    title: 'مدیریت کاربر (مدیر سازمان)',
    icon: '🗂',
    intro: 'حساب شما: کلمهٔ عبور و لوگو/عکسِ پروفایل.',
    sections: [
      {
        title: 'کلمهٔ عبورِ من',
        body: [
          'کلمهٔ عبورِ این حساب را سازمان تعیین کرده است؛ هر زمان خواستید می‌توانید آن را تغییر دهید.',
          '«کلمهٔ عبور جدید» و «تکرار کلمهٔ عبور» را یکسان وارد کنید.',
        ],
      },
      {
        title: 'لوگو / عکس پروفایل',
        body: [
          'با دکمهٔ «انتخاب فایل یا عکس با دوربین» تصویرِ خود را ثبت کنید.',
        ],
      },
    ],
  },

  '/messages': messagesHelp,
  '/edit': editHelp,
  '/about': aboutHelp,
}

/* ═══════════════ مشاور و ناظر ═══════════════ */

const statsCardsHelp = {
  title: 'کارت‌های آماری',
  body: [
    'چهار کارت: تعداد کل قراردادها، تعداد پیمانکاران (یکتا)، پیمانکاران مشترک و غیرمشترک.',
    'با کلیک روی هر کارت، فهرستِ قراردادها بر اساسِ همان فیلتر نمایش داده می‌شود (کارتِ فعال با کادرِ سفید مشخص است).',
  ],
}

const contractsBoardHelp = {
  title: 'گروه‌بندی، مرتب‌سازی و جستجو',
  body: [
    'قراردادها به تفکیکِ پیمانکار گروه‌بندی می‌شوند (سربرگِ هر گروه: نامِ پیمانکار و تعداد قراردادها + نشانِ «★ مشترک»).',
    'مرتب‌سازی: «پیمانکار (سپس تاریخ نزولی)»، «مشترک‌ها در بالا» یا «فقط تاریخ نزولی».',
    'جستجوی زنده در سازمان، شماره، موضوع و پیمانکار (با چند واژه — همه باید پیدا شوند).',
  ],
}

const limitedInfoHelp = {
  title: 'محدودیتِ اطلاعات',
  body: [
    'فقط اطلاعاتِ قراردادِ پیمانکارِ «مشترک» نمایش داده می‌شود؛ برای بقیه، مبالغ و اسناد پنهان است و علتش در کارت نوشته می‌شود.',
  ],
}

const reportPanelHelp = {
  title: 'گزارشِ صورت‌وضعیت',
  body: [
    'با کلیک روی کارتِ قرارداد، پنجرهٔ «صورت‌وضعیت» باز می‌شود: فیلترِ نوع/سال/سند + دکمهٔ «چاپ همه» (PDF یکپارچه) + «دانلود اکسل».',
    'هر صورت‌وضعیت یک گزارشِ مستقل هم دارد (دکمهٔ «گزارشِ صورت‌وضعیت»).',
  ],
}

const CONSULTANT = {
  '/': {
    title: 'قراردادهای تحت مشاوره',
    icon: '🏠',
    intro: 'قراردادهایی که شما به‌عنوان مشاور بر آنها نظارت دارید — وضعیت و گزارش‌های هر قرارداد را ببینید.',
    sections: [
      { title: 'کارت‌های آماری', body: statsCardsHelp.body, tip: statsCardsHelp.body[1] },
      { title: 'گروه‌بندی و مرتب‌سازی', body: contractsBoardHelp.body },
      { title: 'گزارشِ صورت‌وضعیت', body: reportPanelHelp.body },
      { title: 'گزارش پیام‌ها و بازدید', body: [
        'دکمهٔ «گزارش پیام‌ها» روی هر کارت: پیام‌های ردیف‌شده برای همان قرارداد.',
        'دکمهٔ «گزارش بازدید» فقط برای ادمینی دیده می‌شود که از پنلِ مدیریت «ورود به عنوان» کرده باشد.',
      ] },
      { title: 'محدودیتِ اطلاعات', body: limitedInfoHelp.body },
    ],
  },
  '/messages': messagesHelp,
  '/edit': editHelp,
  '/about': aboutHelp,
}

const SUPERVISOR = {
  '/': {
    title: 'قراردادهای تحت نظارت',
    icon: '🏠',
    intro: 'قراردادهایی که شما به‌عنوان ناظر بر آنها نظارت دارید — صورت‌وضعیت‌ها و اسناد قابل مشاهده است.',
    sections: [
      { title: 'کارت‌های آماری', body: statsCardsHelp.body, tip: statsCardsHelp.body[1] },
      { title: 'گروه‌بندی و مرتب‌سازی', body: contractsBoardHelp.body },
      { title: 'گزارشِ صورت‌وضعیت', body: reportPanelHelp.body },
      { title: 'درخواست‌های صورت‌وضعیت و اسناد قرارداد', body: [
        'در کارتِ قرارداد، دکمهٔ «درخواست‌های صورت‌وضعیت»: فهرستِ درخواست‌های همان قرارداد؛ با کلیک روی هر ردیف، گزارشِ همان درخواست باز می‌شود.',
        'دکمهٔ «اسناد قرارداد»: اسنادِ حسابداریِ همان قرارداد.',
      ] },
      { title: 'گزارش پیام‌ها و بازدید', body: [
        'دکمهٔ «گزارش پیام‌ها» روی هر کارت: پیام‌های ردیف‌شده برای همان قرارداد.',
        'دکمهٔ «گزارش بازدید» فقط برای ادمینی دیده می‌شود که از پنلِ مدیریت «ورود به عنوان» کرده باشد.',
      ] },
      { title: 'محدودیتِ اطلاعات', body: limitedInfoHelp.body },
    ],
  },

  '/supervisor/contracts/:contractId/requests': {
    title: 'درخواست‌های صورت‌وضعیت',
    icon: '📄',
    intro: 'فهرستِ درخواست‌های صورت‌وضعیتِ یک قراردادِ تحت نظارتِ شما.',
    sections: [
      {
        title: 'فهرست درخواست‌ها',
        body: [
          'جدول: ردیف، شناسه، موضوع/عنوان، تاریخ و وضعیتِ هر درخواست.',
          'روی هر ردیف کلیک کنید تا صفحهٔ جزئیاتِ همان صورت‌وضعیت باز شود.',
        ],
        tip: 'از منوی کنار، «داشبورد» را بزنید تا دوباره به فهرستِ قراردادهای خود برگردید.',
      },
    ],
  },

  '/supervisor/contracts/:contractId/requests/:requestId': {
    title: 'جزئیاتِ درخواست صورت‌وضعیت',
    icon: '🔍',
    intro: 'جزئیات و مدارکِ یک صورت‌وضعیتِ مشخص.',
    sections: [
      {
        title: 'چه چیزی می‌بینید؟',
        body: [
          'بخشِ «جزئیات»: موضوع، تاریخ و وضعیتِ صورت‌وضعیت.',
          'بخشِ «مدارک»: اسنادِ پیوستِ همان صورت‌وضعیت.',
        ],
      },
    ],
  },

  '/supervisor/contracts/:contractId/documents': {
    title: 'اسنادِ حسابداریِ قرارداد',
    icon: '🗂',
    intro: 'اسنادِ حسابداریِ همان قراردادِ تحت نظارتِ شما.',
    sections: [
      {
        title: 'فهرست اسناد',
        body: [
          'جدولِ اسناد: سال مالی، شمارهٔ سند، شرح، تاریخ سند و مشخصاتِ سندِ مرتبط.',
          'جستجوی زنده و فیلترِ سال مالی در بالای جدول؛ خروجی اکسل هم هست.',
        ],
        tip: 'هر ردیف دکمهٔ «چاپ» دارد که همان سند را چاپ می‌کند.',
      },
    ],
  },

  '/messages': messagesHelp,
  '/edit': editHelp,
  '/about': aboutHelp,
}

/* ═══════════════ ادمین ═══════════════ */

const ADMIN = {
  '/': {
    title: 'داشبورد مدیریتی',
    icon: '📊',
    intro: 'خلاصهٔ وضعیت کل سامانه: سازمانها، کاربران، آپلودها، کاربران ویژه و آنلاینها.',
    sections: [
      {
        title: 'کارت‌های آماری',
        body: [
          'کل سازمان‌ها و کاربران، «دارای آخرین ورود»، «در حال پردازش بارگذاری»، «بارگذاری در ۲ ماه اخیر»، «بازدید در ۲ ماه اخیر»، «کاربران آنلاین (۵ دقیقه اخیر)»، «کاربران ویژهٔ فعال» و «انقضای ویژه در ماه جاری».',
          'روی کارت آنلاینها و انقضا کلیک کنید → لیست جزئیات باز می‌شود.',
          'نمودارِ «بازدیدِ روزانه» ۱۴ روز گذشته را نشان می‌دهد.',
          'داشبورد هر ۶۰ ثانیه به‌صورت خودکار به‌روز می‌شود (بدون رفرشِ صفحه)؛ آخرین زمانِ به‌روزرسانی بالای کارت‌ها نوشته می‌شود.',
        ],
        tip: '«کاربران ویژهٔ فعال» فقط کسانی است که روزهای باقیماندهٔ اشتراکشان مثبت است.',
      },
      {
        title: 'گزارشِ بازدیدها (لاگ حضور)',
        body: [
          'دیالوگِ «بازدیدکنندگان»: آنلاین / امروز / این ماه — به‌همراه زمانِ ورود و زمانِ خروجِ هر کاربر.',
          'این گزارش فقط برای ادمینی دیده می‌شود که از پنلِ مدیریت «ورود به عنوان» کرده باشد.',
        ],
      },
      {
        title: 'پیامک‌ها و کارتِ حواله',
        body: [
          'کارتِ «حوالهٔ پیمانکاران» — حواله‌های ۹۰ روزِ اخیرِ پیمانکارانی که هنوز مشترک نشده‌اند (برای پیگیریِ فروشِ اشتراک).',
          'لاگِ «تاریخچهٔ پیامک‌ها» با نامِ گیرنده و زمانِ ارسال.',
        ],
      },
    ],
  },

  '/organizations': {
    title: 'سازمان‌ها (ادمین)',
    icon: '🏛',
    intro: 'مدیریت دستگاههای اجرایی (سازمانها).',
    sections: [
      {
        title: 'وضعیتها',
        body: [
          'چیپ وضعیت: «فعال»، «بدون ورود» یا «در حال پردازش» (با نوار پیشرفت مرحله‌به‌مرحلهٔ آپلود).',
          'دکمهٔ ⏹ کنار سازمانِ در حال پردازش، پردازش را قبل از پایان متوقف می‌کند (با تأییدیه) و داده‌ها به حالت پیش از آپلود برمی‌گردند.',
        ],
        tip: 'در ستون خطا، آیکن ⓘ متن خطای فارسی آخرین آپلود ناموفق را نشان می‌دهد.',
      },
      {
        title: 'ورود و ویرایش',
        body: [
          'کلید «ورود» = ورود با نقش سازمان. ویرایش = نام اختصاری، نام کاربری، لوگو.',
          'سازمان‌هایی که در حال آپلودِ فایلِ بزرگ هستند چند دقیقه وقت می‌برند.',
        ],
      },
    ],
  },

  '/users': {
    title: 'کاربران (ادمین)',
    icon: '👥',
    intro: 'همهٔ کاربران سامانه: پیمانکار، سازمان، مدیر سازمان، مشاور و ناظر.',
    sections: [
      {
        title: 'جستجو و فیلتر',
        body: [
          'جستجوی زنده (نام کاربری، موبایل، نام) + فیلتر نقش. بدون دکمهٔ جستجو — همان لحظه فیلتر می‌شود.',
          'ارقام فارسی خودکار به لاتین تبدیل می‌شوند و فاصله/نیم‌فاصله مهم نیست.',
        ],
      },
      {
        title: 'عملیات',
        body: [
          'ورود (با نقش کاربر)، ویرایش، حذف. خروجی اکسل هم بالای جدول است.',
          'ستونِ «پیمانکار/مشاور/ناظر» نامِ متناسب با نقش را نشان می‌دهد و ستونِ «اداره/سازمان» سازمانِ مرتبط را.',
        ],
        tip: 'ستون‌هایی که در یک صفحه داده ندارند خودکار پنهان می‌شوند تا جدول نیازی به اسکرولِ افقی نداشته باشد.',
      },
      {
        title: 'ساختِ کاربر با نقشِ «مدیر سازمان»',
        body: [
          'در فرمِ «افزودن کاربر»، نقشِ «مدیر سازمان» را انتخاب کنید و سازمانِ او را مشخص کنید.',
          'نامِ کاربری = کد ملی و رمز را خودِ ادمین تعیین می‌کند؛ مدیر می‌تواند بعداً رمزش را عوض کند.',
        ],
      },
    ],
  },

  '/premium-users': {
    title: 'کاربران ویژه (ادمین)',
    icon: '⭐',
    intro: 'پیمانکارانی که اشتراک فعالی دارند (روزهای باقیماندهٔ مثبت).',
    sections: [
      {
        title: 'افزودن اشتراک دستی',
        body: [
          'برای پرداخت کارت‌به‌کارت: «افزودن کاربر ویژه» → انتخاب کاربر → مبلغ و تاریخ پرداخت.',
          'تاریخ پایان خودکار یکسال بعد محاسبه می‌شود (انباشت تمدیدها).',
        ],
        tip: 'کاربری با روزهای باقیماندهٔ صفر یا منفی دیگر ویژه نیست و در لیست نمی‌آید.',
      },
    ],
  },

  '/payments': {
    title: 'پرداخت‌ها (ادمین)',
    icon: '💳',
    intro: 'همهٔ پرداخت‌های ثبتشدهٔ کاربران سامانه.',
    sections: [
      {
        title: 'عملیات',
        body: [
          'کلید «ورود» کنار هر پرداخت = ورود با نقش پرداخت‌کننده.',
          'خروجی اکسل بالای جدول است.',
        ],
      },
    ],
  },

  '/contractors-payments': {
    title: 'سابقهٔ پرداخت پیمانکاران (ادمین)',
    icon: '💳',
    intro: 'تاریخچهٔ پرداخت‌های اشتراکِ پیمانکاران، با تفکیک بازه و کاربر.',
    sections: [
      {
        title: 'گزارش',
        body: [
          'فیلترِ بازهٔ زمانی + جستجوی کاربر + خروجی اکسلِ کامل.',
        ],
      },
    ],
  },

  '/contractor-stats': {
    title: 'حوالهٔ پیمانکارانِ بی‌مشترک (ادمین)',
    icon: '📈',
    intro: 'حواله‌های ۹۰ روز اخیرِ پیمانکارانی که هنوز مشترک نشده‌اند — برای پیگیریِ فروشِ اشتراک.',
    sections: [
      {
        title: 'تابعی از سازمان',
        body: [
          'با کلیک روی هر سازمان، صفحهٔ آمارِ حواله‌های همان سازمان باز می‌شود.',
          'خروجی اکسل هم در دسترس است.',
        ],
      },
    ],
  },

  '/sms-logs': {
    title: 'تاریخچه پیامک‌ها (ادمین)',
    icon: '📨',
    intro: 'سابقهٔ کامل پیامکهای خروجی سامانه.',
    sections: [
      {
        title: 'فیلتر و جستجو',
        body: [
          'فیلتر نوع پیامک (کد ورود، تایید ورود، حواله جدید، انقضا، پرداخت و ...) + جستجوی زنده در شماره/متن.',
          'ستونِ «نام گیرنده» نامِ واقعیِ گیرنده را نشان می‌دهد (نه کد ملی).',
        ],
      },
      {
        title: 'حذف تا تاریخ',
        body: [
          'دکمهٔ «حذف تا تاریخ» — همهٔ رکوردهای تا پایان تاریخِ انتخابی حذف می‌شوند.',
          'نگهداری سابقهٔ ارسال حداکثر یکسال است؛ بعد از آن خودکار پاک می‌شود.',
        ],
      },
    ],
  },

  '/settings': {
    title: 'تنظیمات (ادمین)',
    icon: '⚙️',
    intro: 'تنظیمات کلی سامانه: درگاه پرداخت، کلید پیامک، شماره فرستنده و ...',
    sections: [
      {
        title: 'نکته',
        body: [
          'تغییرات تنظیمات بلافاصله روی سامانه اعمال می‌شود — با دقت تغییر دهید.',
          'مبلغِ اشتراک، شمارهٔ کارت و شماره‌های تماس از همین‌جاست.',
        ],
      },
    ],
  },

  '/messages': messagesHelp,
  '/edit': editHelp,
  '/about': aboutHelp,
}

/* ═══════════════ نقشهٔ نهایی per-role ═══════════════ */

export const HELP_BY_ROLE = {
  Contractor: CONTRACTOR,
  Department: DEPARTMENT,
  DepartmentManager: DEPARTMENT_MANAGER,
  Consultant: CONSULTANT,
  Supervisor: SUPERVISOR,
  Admin: ADMIN,
}

/** تطبیقِ مسیرِ داینامیک (مثلاً /supervisor/contracts/:contractId/requests) */
const matchPattern = (pattern, path) => {
  if (!pattern.includes(':')) return pattern === path
  try {
    const rx = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$')
    return rx.test(path)
  } catch (e) {
    return false
  }
}

/** محتوای راهنمای یک صفحه برای نقشِ واردشده (یا null) */
export function helpFor(route, role) {
  const byRole = HELP_BY_ROLE[role]
  if (!byRole) return null
  if (byRole[route]) return byRole[route]
  const key = Object.keys(byRole).find((k) => k.includes(':') && matchPattern(k, route))
  return key ? byRole[key] : null
}

/** فهرست صفحاتی که برای این نقش راهنما دارند */
export function roleHelpRoutes(role) {
  const byRole = HELP_BY_ROLE[role]
  return byRole ? Object.keys(byRole) : []
}

/**
 * نقشِ «فعالِ» کاربر:
 * اگر کاربر دو نقش دارد (مثلاً پیمانکار + مشاور) ⇒ نقشی که در «انتخاب نقش» انتخاب کرده
 * (در sessionStorage) ملاک است؛ وگرنه نقشِ اصلیِ حساب.
 */
export function activeRoleOf(userInfo) {
  try {
    const ar = sessionStorage.getItem('activeRole')
    if (ar && HELP_BY_ROLE[ar]) return ar
  } catch (e) { /* noop */ }
  return (userInfo && userInfo.role) || 'Contractor'
}

/* ═══════════════ گامهای تور آموزشی — بر اساس نقش ═══════════════ */

const TOUR_END = {
  target: null,
  title: 'همین! 🎉',
  body: 'در هر صفحه، دکمهٔ «؟» بالای صفحه راهنمای همان صفحه را باز می‌کند؛ صفحهٔ «راهنما» در منو هم کل مستندات را دارد. این تور را هر وقت خواستید از صفحهٔ «مدیریت کاربر» دوباره اجرا کنید.',
}

export const TOUR_STEPS = {
  Contractor: [
    {
      target: 'nav',
      title: 'منوی سامانه',
      body: 'همهٔ بخش‌های سامانه از این منو در دسترس است: داشبورد، مدیریت کاربر، پیام‌ها و راهنما.',
    },
    {
      target: 'profile-card',
      title: 'مشخصات شما',
      body: 'نام، شناسهٔ ملی، شمارهٔ ثبت، کد معاملاتی و کد اقتصادی. دکمهٔ «محدودیت‌ها» محدودیت‌های ثبت‌شدهٔ حسابِ شما را نشان می‌دهد.',
    },
    {
      target: 'introduce-assignment',
      title: 'معرفی ناظر و مشاور',
      body: 'نام و شمارهٔ همراهِ ناظر/مشاورِ خود را وارد کنید. برای او پیامکِ معرفی ارسال می‌شود و پس از پذیرش، او ناظر/مشاورِ آن قرارداد ثبت می‌شود.',
    },
    {
      target: 'contracts-list',
      title: 'قراردادهای شما',
      body: 'هر قرارداد یک کارت است؛ رنگِ چیپ «به‌روزرسانی سازمان» نشان می‌دهد سازمان چقدر اخیر آپلود کرده (🟢 تا ۳۰ روز، 🟠 تا ۹۰ روز، 🔴 قدیمی‌تر).',
    },
    {
      target: 'contractor-search',
      title: 'جستجو و مرتب‌سازی',
      body: 'همان لحظه بین سازمان، شماره و موضوعِ قرارداد جستجو کنید و مرتب‌سازی را عوض کنید. اگر چند سازمان دارید، فیلترِ «سازمان» هم اضافه می‌شود.',
    },
    {
      target: 'contractor-reports',
      title: 'گزارش‌های هر قرارداد',
      body: 'خلاصه، ضمانت‌نامه، سپرده، صورت‌وضعیت، اسناد خزانه، حواله‌ها، اسناد، اعلامیه‌ها، محدودیت‌ها و بیمه‌های پرداختی. دکمهٔ قفل یعنی اشتراک فعال ندارید — با کلیک، صفحهٔ عضویت باز می‌شود.',
    },
    {
      target: 'contractor-financial',
      title: 'گزارش مالی و اکسل',
      body: '«گزارش مالی» یک گزارشِ تجمیعیِ سه‌لایه از همهٔ قراردادهای شماست و «دانلود اکسل» فهرستِ قراردادها را با تاریخ شمسی خروجی می‌گیرد.',
    },
    {
      target: 'text:پیام‌ها',
      title: 'مرکز پیام',
      body: 'پیام‌رسانی با سازمان، ناظر و مشاور. کنارِ هر پیام وضعیتش را می‌بینید: ارسال شده، رسیده، رویت شده.',
    },
    TOUR_END,
  ],

  Consultant: [
    {
      target: 'nav',
      title: 'منوی سامانه',
      body: 'داشبوردِ قراردادها، مدیریت کاربر، پیام‌ها و راهنما از این منو.',
    },
    {
      target: 'profile-card',
      title: 'مشخصات شما',
      body: 'نام، کد ملی و عکس/لوگوی شما در بالای صفحه نمایش داده می‌شود.',
    },
    {
      target: 'stats-cards',
      title: 'کارت‌های آماری',
      body: 'تعداد قراردادها، پیمانکاران، پیمانکاران مشترک و غیرمشترک. با کلیک روی هر کارت، فهرستِ قراردادها فیلتر می‌شود.',
    },
    {
      target: 'contracts-list',
      title: 'قراردادهای تحت مشاوره',
      body: 'قراردادها به تفکیکِ پیمانکار گروه‌بندی شده‌اند؛ مرتب‌سازی و جستجوی زنده هم دارید. کارتِ خاکستری یعنی پیمانکار مشترک نیست و اطلاعاتش نمایش داده نمی‌شود.',
    },
    {
      target: 'contracts-list',
      title: 'صورت‌وضعیت و گزارش‌ها',
      body: 'با کلیک روی کارتِ قرارداد، پنجرهٔ صورت‌وضعیت باز می‌شود: فیلترِ نوع/سال/سند + «چاپ همه» و «دانلود اکسل».',
    },
    {
      target: 'text:پیام‌ها',
      title: 'پیام‌ها',
      body: 'با پیمانکار و سازمانِ همان قرارداد پیام بدهید. برای تبادل با پیمانکار/ناظر/مشاور، انتخابِ قرارداد الزامی است.',
    },
    TOUR_END,
  ],

  Supervisor: [
    {
      target: 'nav',
      title: 'منوی سامانه',
      body: 'داشبوردِ قراردادها، مدیریت کاربر، پیام‌ها و راهنما از این منو.',
    },
    {
      target: 'profile-card',
      title: 'مشخصات شما',
      body: 'نام، کد ملی و عکس/لوگوی شما در بالای صفحه نمایش داده می‌شود.',
    },
    {
      target: 'stats-cards',
      title: 'کارت‌های آماری',
      body: 'تعداد قراردادها، پیمانکاران، پیمانکاران مشترک و غیرمشترک. با کلیک روی هر کارت، فهرستِ قراردادها فیلتر می‌شود.',
    },
    {
      target: 'contracts-list',
      title: 'قراردادهای تحت نظارت',
      body: 'قراردادها به تفکیکِ پیمانکار گروه‌بندی شده‌اند؛ نشانِ «★ مشترک» یعنی اطلاعاتِ آن قرارداد برای شما باز است.',
    },
    {
      target: 'contracts-list',
      title: 'صورت‌وضعیت و گزارش‌ها',
      body: 'با کلیک روی کارت، پنجرهٔ صورت‌وضعیت باز می‌شود: فیلترِ نوع/سال/سند + «چاپ همه» و «دانلود اکسل». دکمه‌های «درخواست‌های صورت‌وضعیت» و «اسناد قرارداد» هم روی کارت هستند.',
    },
    {
      target: 'text:پیام‌ها',
      title: 'پیام‌ها',
      body: 'با پیمانکار و سازمانِ همان قرارداد پیام بدهید. برای تبادل با پیمانکار/ناظر/مشاور، انتخابِ قرارداد الزامی است.',
    },
    TOUR_END,
  ],

  Department: [
    {
      target: 'nav',
      title: 'منوی سامانه',
      body: 'همهٔ بخش‌های سامانه از این منو: داشبورد (آپلود)، پیمانکاران، مدیریت کاربر، پیام‌ها و راهنما.',
    },
    {
      target: 'dept-info',
      title: 'مشخصات سازمان',
      body: 'نام سازمان، شناسهٔ ملی و «آخرین بارگذاری موفق». چیپِ «در حال پردازش» یعنی آپلودِ شما در جریان است.',
    },
    {
      target: 'upload-area',
      title: 'بارگذاری فایل',
      body: 'فایل zip خروجیِ سامانه را در کادر رها کنید و «آپلود» را بزنید. تا پایان پردازش، پیمانکاران و ناظرانِ آن قرارداد اطلاعات را نمی‌بینند.',
    },
    {
      target: 'upload-progress',
      title: 'وضعیت پردازش',
      body: 'نام مرحله، درصد پیشرفت و مرحلهٔ بعد را می‌بینید. اگر «در صف» دیدید یعنی آپلودِ دیگری در جریان است و فایلِ شما بعد از آن پردازش می‌شود.',
    },
    {
      target: 'dept-managers',
      title: 'مدیران سازمان',
      body: 'برای هر مدیر یک حساب بسازید: نامِ کاربری = کد ملیِ او و رمز را خودِ سازمان تعیین می‌کند. مدیر می‌تواند بعداً رمزش را عوض کند.',
    },
    {
      target: 'text:پیمانکاران',
      title: 'پیمانکاران',
      body: 'فهرستِ پیمانکارانِ شما با تعداد قرارداد و حواله. با کلیدِ «ورود» سامانه را از دیدِ پیمانکار می‌بینید.',
    },
    TOUR_END,
  ],

  DepartmentManager: [
    {
      target: 'nav',
      title: 'منوی سامانه',
      body: 'داشبورد، پیمانکاران، مدیریت کاربر، پیام‌ها و راهنما از این منو.',
    },
    {
      target: 'dept-manager-hero',
      title: 'سربرگ و آمار سازمان',
      body: 'نام و نامِ اختصاریِ سازمان، شناسهٔ ملی، «آخرین بارگذاری موفق» و چهار کارتِ آماری: قراردادها، پیمانکاران، حواله‌های یک‌ساله و مدیران.',
    },
    {
      target: 'dept-manager-contractors',
      title: 'جدول پیمانکاران (راست)',
      body: 'پیمانکاران به‌ترتیبِ آخرین حواله فهرست شده‌اند. کادرِ جستجو (با دکمهٔ ×) نام یا کد ملی را می‌گیرد و کلیدِ «ورود» شما را به پنلِ همان پیمانکار می‌برد.',
    },
    {
      target: 'dept-manager-contracts',
      title: 'جدول قراردادها (چپ)',
      body: 'با کلیک روی نامِ پیمانکار، قراردادهای همان پیمانکار اینجا نمایش داده می‌شود: شماره، تاریخ، مبلغ اولیه، افزایش/کاهش، جمع مبلغ، جمع صورت‌وضعیت عادی، پرداخت‌شده و مانده.',
    },
    {
      target: 'text:پیمانکاران',
      title: 'صفحهٔ پیمانکاران',
      body: 'فهرستِ کاملِ پیمانکاران با صفحه‌بندی و جستجو؛ کلیدِ «ورود» هرجا که باشد، با نقشِ همان پیمانکار وارد می‌شوید.',
    },
    {
      target: 'text:پیام‌ها',
      title: 'پیام‌ها',
      body: 'با پیمانکاران، ناظران و مشاورانِ سازمان در ارتباط باشید. برای تبادل با پیمانکار/ناظر/مشاور، انتخابِ قرارداد الزامی است.',
    },
    TOUR_END,
  ],

  Admin: [
    {
      target: 'nav',
      title: 'منوی مدیریت',
      body: 'همهٔ بخش‌های پنلِ مدیریت از این منو: داشبورد، سازمان‌ها، کاربران، کاربران ویژه، پرداخت‌ها، پیامک‌ها، تنظیمات، پیام‌ها و راهنما.',
    },
    {
      target: 'dashboard-cards',
      title: 'آمار کلی سامانه',
      body: 'سازمان‌ها، کاربران، آپلودهای در جریان، بازدیدها، کاربران آنلاین و کاربران ویژه. روی کارتِ آنلاینها و انقضا کلیک کنید تا جزئیات باز شود.',
    },
    {
      target: 'text:سازمان‌ها',
      title: 'سازمان‌ها',
      body: 'وضعیتِ آپلودِ هر سازمان + توقفِ پردازش + ورود با نقشِ سازمان + ویرایشِ حساب.',
    },
    {
      target: 'text:کاربران',
      title: 'کاربران',
      body: 'جستجوی زنده + فیلتر نقش + ورود/ویرایش/حذف + ساختِ «مدیر سازمان» + خروجی اکسل.',
    },
    {
      target: 'text:کاربران ویژه',
      title: 'کاربران ویژه',
      body: 'پیمانکارانِ مشترک. برای پرداختِ کارت‌به‌کارت، از همین‌جا «افزودن کاربر ویژه» را بزنید.',
    },
    {
      target: 'text:پرداخت‌ها',
      title: 'پرداخت‌ها',
      body: 'همهٔ پرداخت‌های ثبت‌شده + ورود با نقشِ پرداخت‌کننده + خروجی اکسل.',
    },
    {
      target: 'text:تاریخچه پیامک‌ها',
      title: 'تاریخچه پیامک‌ها',
      body: 'سابقهٔ کاملِ پیامک‌ها با نامِ گیرنده، فیلترِ نوع و «حذف تا تاریخ».',
    },
    {
      target: 'text:پیام‌ها',
      title: 'مرکز پیام',
      body: 'پیام‌رسانیِ کامل؛ با کاربرانِ سازمان و مدیران بدونِ محدودیتِ قرارداد پیام بدهید.',
    },
    TOUR_END,
  ],
}

/* ═══════════════ سؤالهای متداول ═══════════════ */

/** سؤالهای مشترکِ همهٔ نقشها */
export const FAQ_COMMON = [
  {
    q: 'رمز یا نام کاربریام را فراموش کرده‌ام',
    a: 'با ادمین سامانه تماس بگیرید؛ رمز از پنل مدیریت قابل بازنشانی است.',
  },
  {
    q: 'چرا مجبورم رمز عبورم را عوض کنم؟',
    a: 'حساب‌هایی که رمزِ اولیه‌شان برابرِ نام کاربری (شناسهٔ ملی) بوده، در اولین ورود باید رمز تازه‌ای انتخاب کنند که با شناسهٔ ملی یکسان نباشد.',
  },
  {
    q: 'شمارهٔ تلفن همراهم اجباری است؟',
    a: 'بله، اما فقط برای خودِ شما. تا زمانی که شمارهٔ همراهِ خود را در «مدیریت کاربر → ویرایش مشخصات» ثبت نکنید، پنل کامل باز نمی‌شود. شماره باید ۱۱ رقم و با ۰۹ آغاز شود. وقتی ادمین یا سازمان از پنلِ خودش واردِ پنلِ شما می‌شود، این اجبار اعمال نمی‌شود.',
  },
  {
    q: 'چطور لوگو یا عکس خودم را ثبت کنم؟',
    a: 'موقع ورود، سامانه پیشنهاد می‌کند تصویرتان را ثبت کنید: روی موبایل دکمهٔ «گرفتن عکس با دوربین» و روی کامپیوتر یک کیوآرکد که با موبایل اسکن می‌شود. بعداً هم از «مدیریت کاربر» قابل تغییر است.',
  },
  {
    q: 'چرا پیامک «تایید ورود موفق» دریافت کردم؟',
    a: 'این پیامک در هر ورودِ انسانی به شمارهٔ ثبت‌شدهٔ شما ارسال می‌شود تا از ورود شما مطمئن شویم. اگر شماره‌تان اشتباه است، آن را در «مدیریت کاربر» اصلاح کنید.',
  },
  {
    q: 'پیام «در صف» یعنی چه؟',
    a: 'آپلودِ دیگری در جریان است؛ فایلِ شما بعد از آن پردازش می‌شود. نوبت که رسید، نوار پیشرفت شما شروع می‌شود.',
  },
  {
    q: 'نوتیفِ «پیام جدید» چرا متن پیام را نشان نمی‌دهد؟',
    a: 'نوتیف فقط اطلاع‌رسانی است (به‌دلیل محرمانگی)؛ برای دیدنِ متن، واردِ «پیام‌ها» شوید. نوتیف روی کامپیوتر و موبایل کار می‌کند و پیامکِ آن ارسال نمی‌شود.',
  },
  {
    q: 'وضعیتِ «ارسال شده / رسیده / رویت شده» یعنی چه؟',
    a: '«ارسال شده» = پیام در سامانه ثبت شده؛ «رسیده» = طرفِ مقابل گفتگو را دیده؛ «رویت شده» = پیام را باز کرده است.',
  },
]

/** سؤالهای ویژهٔ هر نقش */
export const FAQ_BY_ROLE = {
  Contractor: [
    {
      q: 'چرا بعضی از اطلاعاتِ قراردادم را نمی‌بینم؟',
      a: 'دیدنِ کاملِ اطلاعاتِ قرارداد (مبالغ، اسناد، حواله‌ها) به اشتراکِ فعال بستگی دارد. اگر مشترک نیستید، با کلیک روی یکی از دکمه‌های گزارش، صفحهٔ عضویت باز می‌شود.',
    },
    {
      q: 'چطور عضویت (اشتراک) تهیه کنم؟',
      a: 'دورهٔ یک‌ساله یا دوساله را انتخاب کنید و «پرداخت آنلاین» را بزنید. اگر قبلاً اشتراکِ فعال داشته باشید، باقیماندهٔ دورهٔ قبلی از بین نمی‌رود و به دورهٔ جدید اضافه می‌شود.',
    },
    {
      q: 'درگاه بانکی باز نشد؛ چه کنم؟',
      a: 'اگر پرداخت آنلاین انجام نشود، صفحهٔ پرداخت یک کادر زرد با عنوان «پرداخت آنلاین انجام نشد — راه جایگزین: کارت به کارت» نشان می‌دهد. با زدنِ دکمهٔ «مشاهدهٔ شماره کارت و مراحل پرداخت»، شمارهٔ کارت نمایش داده می‌شود؛ مبلغ را واریز کنید و شناسهٔ ملی و نامِ شرکت را پیامک کنید. ثبتِ نهایی با ادمین است.',
    },
    {
      q: 'گزارش‌های هر قرارداد کجا هستند؟',
      a: 'زیر هر کارتِ قرارداد دکمه‌های رنگیِ گزارش‌ها را می‌بینید: خلاصه، ضمانت‌نامه، سپرده، صورت‌وضعیت، اسناد خزانه، حواله‌ها، اسناد، اعلامیه‌ها، محدودیت‌ها و بیمه‌های پرداختی. هر گزارش جستجو و فیلترِ سال مالی هم دارد.',
    },
    {
      q: 'چطور پیمانکارِ خودم را به ناظر/مشاور معرفی کنم؟',
      a: 'در کارتِ «معرفی ناظر/مشاور» بالای فهرستِ قراردادها، نام و شمارهٔ همراهِ ناظر یا مشاور را وارد کنید. برای او پیامکِ معرفی ارسال می‌شود؛ او با پاسخِ ۱ می‌پذیرد و با ۲ رد می‌کند.',
    },
    {
      q: '«گزارش پیام‌ها» و «گزارش بازدید» چه فرقی دارند؟',
      a: '«گزارش پیام‌ها» پیام‌های ردیف‌شده برای همان قرارداد را نشان می‌دهد. «گزارش بازدید» فقط برای ادمینی دیده می‌شود که از پنلِ مدیریت «ورود به عنوان» کرده باشد.',
    },
  ],
  Department: [
    {
      q: 'چطور فایل را آپلود کنم؟',
      a: 'در صفحهٔ اصلی، فایل zip خروجیِ نرم‌افزارِ جام ارجان را در کادر «بارگذاری فایل» رها کنید و «آپلود» را بزنید. پس از آپلود، پردازش خودکار شروع می‌شود و مرحله و درصد پیشرفت را می‌بینید.',
    },
    {
      q: 'چرا پردازش آپلود طول می‌کشد؟',
      a: 'فایل‌های بزرگ (مثل اسناد ۳۰۰ مگابایتی) چند دقیقه پردازش می‌خواهند — مرحلهٔ «اسناد» طولانی‌ترین است. نوار پیشرفت و نام مرحله زیر «در حال پردازش» نشان داده می‌شود.',
    },
    {
      q: 'چرا پروگرس‌بار و نام مرحله را نمی‌بینم؟',
      a: 'اگر سازمان در حال پردازش است و چیزی نمی‌بینید، صفحه را رفرش کنید (Ctrl+F5). اگر پردازش تمام شده باشد نوارها حذف می‌شوند — عادی است.',
    },
    {
      q: 'نام کاربری و رمز حساب سازمان من چیست؟',
      a: 'نام کاربری حساب سازمان، شناسهٔ ملیِ همان سازمان است. رمز و نام کاربری را از «مدیریت کاربر → تنظیمات حساب سازمان» تغییر دهید.',
    },
    {
      q: 'چطور برای مدیران سازمان حساب بسازم؟',
      a: 'در کارتِ «مدیران سازمان» پایین صفحهٔ اصلی، «افزودن مدیر» را بزنید: کد ملی (نام کاربری)، نام و نام خانوادگی، شمارهٔ تلفن و رمز. رمز را خودِ سازمان تعیین می‌کند و مدیر می‌تواند بعداً آن را عوض کند.',
    },
    {
      q: 'آیا فایلِ آپلودی اطلاعات ناظران را هم به‌روز می‌کند؟',
      a: 'بله — اگر در فایل خروجی، کد ملی/نام/شمارهٔ همراهِ ناظر آمده باشد، سامانه آن را ثبت و در صورت خالی‌بودن موبایلِ ناظر، شمارهٔ همراهش را هم کامل می‌کند.',
    },
  ],
  DepartmentManager: [
    {
      q: 'تفاوتِ من با «کاربرِ سازمان» چیست؟',
      a: 'همهٔ امکاناتِ کاربرِ سازمان را دارید بجزِ آپلودِ اطلاعات. ساخت و حذفِ مدیران هم در اختیارِ خودِ کاربرِ سازمان است.',
    },
    {
      q: 'کلیدِ «ورود» در جدولِ پیمانکاران چه می‌کند؟',
      a: 'شما را با نقشِ همان پیمانکار واردِ پنلِ او می‌کند تا وضعیت را از دیدِ او ببینید. با «خروج» به داشبوردِ خودِ شما برمی‌گردید (خروجِ کامل انجام نمی‌شود).',
    },
    {
      q: 'ستونِ «مانده» چطور حساب می‌شود؟',
      a: 'مانده = جمع مبلغِ قرارداد (مبلغ اولیه + افزایش/کاهش) منهای پرداخت‌شده.',
    },
    {
      q: 'چطور رمزِ خودم را عوض کنم؟',
      a: 'از منوی «مدیریت کاربر» → کادرِ «کلمه عبورِ من». رمزِ اولیه را سازمان تعیین کرده است و شما هر زمان می‌توانید آن را تغییر دهید.',
    },
  ],
  Consultant: [
    {
      q: 'داشبوردِ بالای صفحه چه چیزهایی نشان می‌دهد؟',
      a: 'چهار کارت: تعداد کل قراردادها، تعداد پیمانکاران (یکتا)، تعداد پیمانکاران مشترک و تعداد پیمانکاران غیرمشترک. با کلیک روی هر کارت، فهرستِ قراردادها بر اساسِ همان فیلتر نمایش داده می‌شود.',
    },
    {
      q: 'چرا اطلاعاتِ قراردادِ پیمانکارِ غیرمشترک برای من پنهان است؟',
      a: 'قاعدهٔ سامانه این است که ناظر و مشاور فقط اطلاعاتِ قراردادِ پیمانکارِ «مشترک» را ببینند. برای بقیه، مبالغ و اسناد نمایش داده نمی‌شود و علتش در کارت نوشته می‌شود.',
    },
    {
      q: 'چطور یک قرارداد را در فهرست پیدا کنم؟',
      a: 'کادر جستجو را استفاده کنید؛ می‌توانید شمارهٔ قرارداد، موضوع، نامِ پیمانکار یا سازمان را بنویسید (چند واژه هم‌زمان هم می‌شود — همه باید با هم پیدا شوند).',
    },
    {
      q: 'برای پیام دادن به پیمانکار باید قرارداد انتخاب کنم؟',
      a: 'بله. تبادلِ پیام میان پیمانکار/ناظر/مشاور باید مربوط به یک قراردادِ مشخص باشد؛ ولی پیام به سازمان، مدیرِ سازمان یا ادمین بدونِ قرارداد هم مجاز است.',
    },
  ],
  Supervisor: [
    {
      q: 'داشبوردِ بالای صفحه چه چیزهایی نشان می‌دهد؟',
      a: 'چهار کارت: تعداد کل قراردادها، تعداد پیمانکاران (یکتا)، پیمانکاران مشترک و پیمانکاران غیرمشترک. با کلیک روی هر کارت، فهرستِ قراردادها فیلتر می‌شود.',
    },
    {
      q: 'اگر پیمانکاری من را معرفی کرد چه کار کنم؟',
      a: 'پیامکِ معرفی برایتان ارسال می‌شود؛ با ارسالِ عدد ۱ آن را می‌پذیرید و با ۲ رد می‌کنید. پس از پذیرش، قراردادها در داشبوردِ شما نمایش داده می‌شوند.',
    },
    {
      q: 'چرا فقط بعضی قراردادها را کامل می‌بینم؟',
      a: 'قاعدهٔ سامانه: ناظر و مشاور فقط اطلاعاتِ قراردادِ پیمانکارِ «مشترک» را می‌بینند. برای پیمانکارِ غیرمشترک، مبالغ و اسناد پنهان می‌شود و علتش در کارت نوشته می‌شود.',
    },
    {
      q: 'شمارهٔ همراهم ثبت نشده و پیامک معرفی نمی‌رسد؛ چه کنم؟',
      a: 'شمارهٔ همراه شما اجباری است. در «مدیریت کاربر → ویرایش مشخصات» آن را ثبت کنید؛ سپس در صورت نیاز پیمانکار می‌تواند دوباره شما را معرفی کند.',
    },
    {
      q: '«درخواست‌های صورت‌وضعیت» و «اسناد قرارداد» کجا هستند؟',
      a: 'روی کارتِ هر قرارداد: «درخواست‌های صورت‌وضعیت» فهرستِ درخواست‌های همان قرارداد را با گزارشِ مستقلِ هر ردیف نشان می‌دهد و «اسناد قرارداد» اسنادِ حسابداریِ همان قرارداد را.',
    },
  ],
  Admin: [
    {
      q: 'چطور به‌عنوان یک کاربر وارد شوم (بدون پیامک)؟',
      a: 'در پنل مدیریت، «کاربران» یا «پیمانکاران» ⇒ دکمهٔ «ورود به عنوان». این روش اصلاً ورودِ کاربر انجام نمی‌دهد و فقط توکن صادر می‌کند ⇒ هیچ پیامکی ارسال نمی‌شود. با دکمهٔ «خروج» به پنل ادمین برمی‌گردید.',
    },
    {
      q: '«گزارش بازدید» را چه کسانی می‌بینند؟',
      a: 'فقط ادمینی که از پنلِ مدیریت با نقشِ پیمانکار/مشاور/ناظر/کاربرِ سازمان «ورود به عنوان» کرده باشد. در آن حالت، دکمهٔ «گزارش بازدید» روی کارتِ قرارداد ظاهر می‌شود و زمانِ ورود و خروج را نشان می‌دهد.',
    },
    {
      q: 'جستجوی لیست کاربران چطور کار می‌کند؟',
      a: 'هر واژه‌ای که بنویسید باید در رکورد پیدا شود (به‌صورت AND). ارقام فارسی خودکار به لاتین تبدیل می‌شوند و فاصله/نیم‌فاصله مهم نیست (مثلاً «اکبر ماس پی» = «اکبر ماس‌پی»). نامِ ناظر، مشاور، پیمانکار و سازمان همه جستجو می‌شوند.',
    },
    {
      q: 'چرا ستون‌های جدولِ کاربران کم و زیاد می‌شود؟',
      a: 'ستون‌هایی که در صفحهٔ جاری داده ندارند خودکار پنهان می‌شوند تا جدول نیازی به اسکرولِ افقی نداشته باشد؛ با تغییرِ صفحه، ستون‌های مربوطه برمی‌گردند.',
    },
    {
      q: 'چطور مدیر سازمان بسازم؟',
      a: 'در «کاربران» → «افزودن کاربر»، نقشِ «مدیر سازمان» را انتخاب و سازمانِ او را مشخص کنید. نامِ کاربری = کد ملی است و رمز را خودِ ادمین تعیین می‌کند.',
    },
    {
      q: 'کارت «حوالهٔ پیمانکاران» در داشبورد چه می‌گوید؟',
      a: 'حواله‌های ۹۰ روزِ اخیرِ پیمانکارانی که هنوز مشترک نشده‌اند را به تفکیک سازمان نشان می‌دهد — برای پیگیریِ فروشِ اشتراک. با کلیک روی هر سازمان، صفحهٔ آمارِ حواله‌ها باز می‌شود.',
    },
  ],
}

/** همهٔ سؤالها برای یک نقش (مشترک + ویژهٔ نقش) */
export function faqFor(role) {
  return [...FAQ_COMMON, ...(FAQ_BY_ROLE[role] || [])]
}

/** فهرستِ قدیمی (همهٔ سؤالها) — برای سازگاری */
export const FAQ = [...FAQ_COMMON, ...Object.values(FAQ_BY_ROLE).flat()]
